<template>
	<!-- Confirmation Modal -->
	<NotificationModal
		:is-open="showDismissModal"
		type="warning"
		title="Test verwerfen?"
		message="Möchten Sie den gespeicherten Test wirklich verwerfen? Alle bisherigen Antworten gehen verloren."
		confirm-text="Verwerfen"
		cancel-text="Abbrechen"
		:show-cancel="true"
		@confirm="confirmDismiss"
		@cancel="showDismissModal = false"
		@close="showDismissModal = false"
	/>

	<div
		v-if="show && progressSummary"
		class="text-white shadow-md border-b border-indigo-700 bg-gradient-to-r from-blue-600 to-indigo-600"
	>
		<div class="container px-4 py-4 mx-auto">
			<div class="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between">
				<!-- Left: Info -->
				<div class="flex items-center gap-3 sm:gap-4">
					<div class="flex-shrink-0">
						<div class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full sm:w-12 sm:h-12 bg-white/20">
							<svg class="w-5 h-5 text-white sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
					</div>
					<div class="flex-1 min-w-0">
						<h3 class="text-base font-semibold sm:text-lg">Unvollständiger Test gefunden</h3>
						<p class="text-xs text-blue-100 sm:text-sm">
							Test für <strong>{{ progressSummary.childName }}</strong> •
							{{ progressSummary.progress }} beantwortet
						</p>
						<p class="mt-1 text-xs text-blue-200 sm:inline hidden">
							💾 Automatisch gespeichert
						</p>
					</div>
				</div>

				<!-- Right: Actions -->
				<div class="flex items-center self-end gap-3 sm:self-center">
					<!-- Progress Circle (hidden on mobile) -->
					<div class="relative hidden w-16 h-16 sm:block">
						<svg class="w-16 h-16 transform -rotate-90">
							<circle
								cx="32"
								cy="32"
								r="28"
								stroke="currentColor"
								stroke-width="4"
								fill="none"
								class="text-white/20"
							/>
							<circle
								cx="32"
								cy="32"
								r="28"
								stroke="currentColor"
								stroke-width="4"
								fill="none"
								class="text-white"
								:stroke-dasharray="circumference"
								:stroke-dashoffset="progressOffset"
								stroke-linecap="round"
							/>
						</svg>
						<div class="absolute inset-0 flex items-center justify-center">
							<span class="text-sm font-bold">{{ progressSummary.progressPercent }}%</span>
						</div>
					</div>

					<!-- Buttons -->
					<div class="flex gap-2">
						<button
							@click="handleResume"
							class="px-4 py-2 text-sm font-semibold text-blue-600 transition-all duration-200 transform bg-white rounded-lg shadow-md sm:px-6 sm:py-3 hover:shadow-lg hover:scale-105"
						>
							<span class="flex items-center gap-1 sm:gap-2">
								<svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
									/>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								Fortsetzen
							</span>
						</button>

						<button
							@click="handleDismiss"
							class="px-3 py-2 text-white transition-colors duration-200 rounded-lg sm:px-4 sm:py-3 bg-white/10 hover:bg-white/20"
							title="Verwerfen"
						>
							<svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useTestFlowCoordination } from '@/stores/coordination/test-flow';
import { useWizardStore } from '@/stores/wizard';
import { useTracking } from '@/services/tracking';
import NotificationModal from '@/components/NotificationModal.vue';
import { TOTAL_QUESTIONS } from '@/data/learningTest';

interface Props {
	show?: boolean;
}

withDefaults(defineProps<Props>(), {
	show: true,
});

const emit = defineEmits<{
	resume: [];
	dismiss: [];
}>();

const coordination = useTestFlowCoordination();
const wizard = useWizardStore();
const tracking = useTracking();
const showDismissModal = ref(false);
const isDismissed = ref(false); // Track if user dismissed the banner

// Build progress summary from coordination layer (uses actual Pinia data)
const progressSummary = computed(() => {
	// If user dismissed the banner, don't show it
	if (isDismissed.value) {
		return null;
	}
	
	const flowState = coordination.flowState.value;
	
	// Only show banner for test-in-progress or onboarding-complete
	if (flowState !== 'test-in-progress' && flowState !== 'onboarding-complete') {
		return null;
	}
	
	const answeredCount = coordination.answeredQuestionsCount.value;
	const progressPercent = Math.round((answeredCount / TOTAL_QUESTIONS) * 100);
	
	return {
		childName: wizard.basicInfo.childName || 'Test',
		progress: `${answeredCount} von ${TOTAL_QUESTIONS} Fragen`,
		progressPercent,
		lastSaved: 'Jetzt', // Could track this if needed
		daysUntilExpiry: 7, // Default expiry
	};
});

// Progress circle calculation
const circumference = 2 * Math.PI * 28; // radius = 28
const progressOffset = computed(() => {
	if (!progressSummary.value) return circumference;
	const progress = progressSummary.value.progressPercent;
	return circumference - (progress / 100) * circumference;
});

/**
 * Handle resume test button click
 */
function handleResume() {
	// Track test resumed via wizard event
	tracking.trackWizardEvent({
		eventType: 'test_resumed' as any, // TrackingEventType.TEST_RESUMED
		metadata: {
			answeredQuestions: coordination.answeredQuestionsCount.value,
			totalQuestions: TOTAL_QUESTIONS,
			progressPercent: progressSummary.value?.progressPercent || 0,
			flowState: coordination.flowState.value,
			source: 'resume_banner',
		},
	});
	
	emit('resume');
}

/**
 * Show confirmation modal before dismissing
 */
function handleDismiss() {
	showDismissModal.value = true;
}

/**
 * Confirm dismissal - clear all test data and track event
 */
function confirmDismiss() {
	const answeredCount = coordination.answeredQuestionsCount.value;
	
	// Track test abandoned via wizard event
	tracking.trackWizardEvent({
		eventType: 'test_abandoned' as any, // TrackingEventType.TEST_ABANDONED
		metadata: {
			answeredQuestions: answeredCount,
			totalQuestions: TOTAL_QUESTIONS,
			progressPercent: progressSummary.value?.progressPercent || 0,
			reason: 'user_dismissed_banner',
			flowState: coordination.flowState.value,
		},
	});
	
	// Clear wizard store data
	wizard.reset();
	
	// Hide banner immediately
	isDismissed.value = true;
	
	console.log('🗑️ Test dismissed by user - all data cleared');
	
	showDismissModal.value = false;
	emit('dismiss');
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease-out;
}

.slide-down-enter-from {
	transform: translateY(-100%);
	opacity: 0;
}

.slide-down-leave-to {
	transform: translateY(-100%);
	opacity: 0;
}
</style>
